export const PREVIEW_LABEL = 'Preview image — interactive 3D unavailable';
export const CAMERA_NAMES = Object.freeze(['front', 'oblique', 'channel']);
export const CAMERA_NEAR_METRES = 0.03;
export const CAMERA_FAR_METRES = 60;
export const REFERENCE_ASPECT = 847 / 362;
export const CAMERA_LIMITS = Object.freeze({
  overview: Object.freeze({minDistance: 12, maxDistance: 28, minPolarAngle: 0.48, maxPolarAngle: 1.45, minAzimuthAngle: -0.95, maxAzimuthAngle: 0.95}),
  channel: Object.freeze({minDistance: 4.8, maxDistance: 10, minPolarAngle: 1.32, maxPolarAngle: 1.54, minAzimuthAngle: -0.15, maxAzimuthAngle: 0.15}),
});

export function cameraLimits(name) {
  if (!CAMERA_NAMES.includes(name)) throw new Error('Unknown camera preset');
  return CAMERA_LIMITS[name === 'channel' ? 'channel' : 'overview'];
}

export function responsiveFov(baseFov, aspect) {
  if (!(baseFov > 0 && baseFov < 90 && aspect > 0 && Number.isFinite(aspect))) throw new Error('Invalid camera framing');
  return Math.min(80, 2 * Math.atan(Math.tan(baseFov * Math.PI / 360) * Math.max(1, REFERENCE_ASPECT / aspect)) * 180 / Math.PI);
}

export function validateManifest(manifest) {
  if (manifest?.schemaVersion !== 1 || manifest.reviewedFactorySHA256 !== 'bfa569f080f8ee471237ca2644c23ed862ac98024cbec0d8aea0bb9de716fa9e') throw new Error('Unknown reviewed scene');
  const names = {assets: ['rockSet', 'substrate', 'ferns', 'scanRocks', 'rockSurface'], data: ['watercourse', 'fourMassComposition', 'rockMaterialDirection'], previews: ['front']};
  for (const [kind, required] of Object.entries(names)) {
    if (JSON.stringify(Object.keys(manifest[kind] ?? {}).sort()) !== JSON.stringify(required.sort())) throw new Error(`Invalid ${kind} bindings`);
    for (const binding of Object.values(manifest[kind])) {
      const prefix = kind === 'data' ? 'data/terrarium/' : 'assets/terrarium/';
      if (typeof binding.path !== 'string' || !binding.path.startsWith(prefix) || !/^[a-zA-Z0-9/_.-]+$/.test(binding.path) || binding.path.split('/').includes('..') || !/^[a-f0-9]{64}$/.test(binding.sha256) || !Number.isSafeInteger(binding.bytes) || binding.bytes <= 0) throw new Error('Invalid local binding');
    }
  }
  return manifest;
}

export async function verifyBytes(bytes, binding, subtle = globalThis.crypto?.subtle) {
  if (!(bytes instanceof ArrayBuffer) || bytes.byteLength !== binding.bytes) throw new Error('Asset size check failed');
  if (!subtle) throw new Error('Secure asset verification is unavailable');
  const digest = await subtle.digest('SHA-256', bytes);
  const hex = Array.from(new Uint8Array(digest), n => n.toString(16).padStart(2, '0')).join('');
  if (hex !== binding.sha256) throw new Error('Asset integrity check failed');
  return bytes;
}

export function validateEmbeddedGLB(bytes) {
  const view = new DataView(bytes);
  if (view.byteLength < 20 || view.getUint32(0, true) !== 0x46546c67 || view.getUint32(4, true) !== 2 || view.getUint32(8, true) !== view.byteLength || view.getUint32(16, true) !== 0x4e4f534a || view.getUint32(12, true) > view.byteLength - 20) throw new Error('Invalid GLB container');
  const json = JSON.parse(new TextDecoder('utf-8', {fatal: true}).decode(new Uint8Array(bytes, 20, view.getUint32(12, true))));
  for (const source of [...(json.buffers ?? []), ...(json.images ?? [])]) {
    if (source.uri && !source.uri.startsWith('data:')) throw new Error('External GLB resources are not allowed');
  }
  return json;
}

export async function fetchVerified(binding, {signal, fetcher = globalThis.fetch, baseURL = globalThis.document?.baseURI, subtle} = {}) {
  const url = new URL(binding.path, baseURL);
  if (url.origin !== new URL(baseURL).origin) throw new Error('Assets must be same-origin');
  signal?.throwIfAborted();
  const response = await fetcher(url.href, {signal, credentials: 'same-origin', redirect: 'error'});
  if (!response.ok) throw new Error('A preview asset could not be loaded');
  const bytes = await response.arrayBuffer();
  await verifyBytes(bytes, binding, subtle);
  signal?.throwIfAborted();
  return bytes;
}

export async function loadVerifiedInputs(manifest, {parseGLB, onProgress = () => {}, onAsset = () => {}, ...options}) {
  validateManifest(manifest);
  const assets = {}, data = {};
  let completed = 0;
  const total = Object.keys(manifest.assets).length + Object.keys(manifest.data).length;
  for (const [name, binding] of Object.entries(manifest.data)) {
    const bytes = await fetchVerified(binding, options);
    data[name] = JSON.parse(new TextDecoder('utf-8', {fatal: true}).decode(bytes));
    onProgress(++completed, total);
  }
  for (const [name, binding] of Object.entries(manifest.assets)) {
    const bytes = await fetchVerified(binding, options);
    validateEmbeddedGLB(bytes);
    const asset = await parseGLB(bytes);
    onAsset(asset);
    options.signal?.throwIfAborted();
    asset.verifiedSHA256 = binding.sha256;
    assets[name] = asset;
    onProgress(++completed, total);
  }
  return {assets, data};
}

export function disposeSceneResources(roots) {
  const geometries = new Set(), materials = new Set(), textures = new Set();
  for (const root of roots) root?.traverse?.(object => {
    if (object.geometry) geometries.add(object.geometry);
    for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
      if (!material) continue;
      materials.add(material);
      for (const value of Object.values(material)) if (value?.isTexture) textures.add(value);
      for (const uniform of Object.values(material.uniforms ?? {})) if (uniform?.value?.isTexture) textures.add(uniform.value);
    }
  });
  textures.forEach(texture => { texture.dispose(); texture.source?.data?.close?.(); });
  materials.forEach(material => material.dispose());
  geometries.forEach(geometry => geometry.dispose());
}
